import roleManager from './roleManager.js';
import { USER_ROLES, STORAGE_KEYS } from './constants.js';

/**
 * 테스트용 기본 사용자 역할 설정
 */
export const setDefaultUserRole = (role = USER_ROLES.ADMIN) => {
  const defaultUser = {
    id: 'test-user',
    username: 'test',
    role: role,
    email: 'test@example.com'
  };
  
  localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(defaultUser));
  return defaultUser;
};

/**
 * 사용자가 특정 페이지에 접근할 수 있는지 확인
 * @param {string} path - 접근하려는 페이지 경로
 * @returns {boolean} 접근 가능 여부
 */
export const hasPageAccess = (path) => {
  // roleManager는 로컬 스토리지에서 피처 권한을 로드하므로 최신 상태로 갱신
  roleManager.refreshFeatures();
  return roleManager.hasPageAccess(path);
};

/**
 * 사용자가 특정 메뉴에 접근할 수 있는지 확인
 * @param {string} menuLabel - 메뉴 라벨
 * @returns {boolean} 접근 가능 여부
 */
export const hasMenuAccess = (menuLabel) => {
  // roleManager는 로컬 스토리지에서 피처 권한을 로드하므로 최신 상태로 갱신
  roleManager.refreshFeatures();
  if (isSystemAdmin()) {
    return true;
  }
  return roleManager.hasMenuAccess(menuLabel);
};

/**
 * 사용자가 시스템 관리자인지 확인
 * @returns {boolean} 시스템 관리자 여부
 */
export const isSystemAdmin = () => {
  const userRoles = getCurrentUserRoles();
  return userRoles?.includes(USER_ROLES.SYSTEM_ADMIN);
};

/**
 * 사용자가 특정 피처에 접근할 수 있는지 확인
 * @param {string} featureName - 피처 식별자 (예: 'DASHBOARD')
 * @returns {boolean} 접근 가능 여부
 */
export const hasFeatureAccess = (featureName) => {
  roleManager.refreshFeatures();
  return roleManager.hasFeatureAccess(featureName);
};

/**
 * 현재 사용자의 역할 배열을 가져오는 함수
 * @returns {string[]} 사용자 역할 배열 (기본값: 빈 배열)
 */
export const getCurrentUserRoles = () => {
  try {
    const userRoles = localStorage.getItem(STORAGE_KEYS.USER_ROLES);
    if (userRoles) {
      return JSON.parse(userRoles);
    }
  } catch (error) {
    console.error('사용자 역할 데이터 파싱 오류:', error);
  }
  
  return [];
};

/**
 * 현재 사용자의 피처 권한 배열을 가져오는 함수
 * @returns {string[]} 피처 권한 배열 (기본값: 빈 배열)
 */
export const getCurrentUserFeatures = () => {
  try {
    const userFeatures = localStorage.getItem(STORAGE_KEYS.USER_FEATURES);
    if (userFeatures) {
      return JSON.parse(userFeatures);
    }
  } catch (error) {
    console.error('피처 권한 데이터 파싱 오류:', error);
  }
  
  return [];
};

/**
 * 현재 사용자의 역할을 가져오는 함수 (하위 호환성 유지)
 * @returns {string} 사용자 역할 (기본값: GUEST)
 * @deprecated 서버 기반 권한 시스템에서는 getCurrentUserRoles()를 사용하세요
 */
export const getCurrentUserRole = () => {
  const roles = getCurrentUserRoles();
  
  // 역할 배열이 있으면 첫 번째 역할 반환 (하위 호환성)
  if (roles && roles.length > 0) {
    return roles.join(', ');
  }
  
  return USER_ROLES.GUEST;
};

/**
 * 사용자가 로그인되어 있는지 확인
 * @returns {boolean} 로그인 상태
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  return !!token;
};

/**
 * 관리자 권한 확인
 * @param {string} userRole - 사용자 역할
 * @returns {boolean} 관리자 여부
 */
export const isAdmin = (userRole) => {
  return userRole === USER_ROLES.ADMIN;
};

/**
 * 일반 사용자 권한 확인
 * @param {string} userRole - 사용자 역할
 * @returns {boolean} 일반 사용자 여부
 */
export const isUser = (userRole) => {
  return userRole === USER_ROLES.USER || userRole === USER_ROLES.ADMIN;
}; 